import FormEmailListField from '../../../../components/form/FormEmailListField';
import { __ } from '../../../../utils';
import {
  Button,
  Dialog,
  Divider,
  Stack,
  SwitchField,
  Typography,
} from '@presov.sk/presov-ui-react';
import React from 'react';
import { Field, Form } from 'react-final-form';

type Props = {
  open: boolean;
  reportEnabled: boolean;
  reportEmails: string[];
  /** Uzavreté hlasovanie report odošle hneď po uložení, nie neskôr. */
  isOver: boolean;
  loading?: boolean;
  onCancel: () => void;
  onConfirm: (values: { enabled: boolean; emails: string[] }) => void;
};

type Values = { sendReport: boolean; reportEmails: string[] };

/**
 * Nastavenie reportu na už otvorenom hlasovaní.
 *
 * **Tretia vec, ktorá sa na bežiacom hlasovaní dá zmeniť** - popri viditeľnosti
 * a termíne uzavretia. Dôvod je ten istý ako pri nich: o tom, komu výsledky
 * pošleme, sa mesto rozhoduje väčšinou až vtedy, keď je čo posielať, a celé
 * hlasovanie je od publikovania zamknuté (`VOTING_ALREADY_ACTIVE`).
 *
 * Telo požiadavky je **celé nastavenie**, nie zmena po jednej adrese - preto
 * dialóg odosiela oba kľúče naraz a `PUT /admin/votings/{id}/report` zoznam
 * nahrádza.
 */
const VotingReportDialog: React.FC<Props> = ({
  open,
  reportEnabled,
  reportEmails,
  isOver,
  loading,
  onCancel,
  onConfirm,
}) => {
  /**
   * Stabilná referencia - `react-final-form` porovnáva `initialValues` plytko
   * a pri zmene formulár reinicializuje (detail v `.docs/vyvoj/formulare.md`).
   * Pole adries je tu navyše vnorená hodnota, takže by sa plytko nikdy
   * nevyhodnotilo ako rovnaké.
   */
  const initialValues = React.useMemo<Values>(
    () => ({ sendReport: reportEnabled, reportEmails }),
    [reportEnabled, reportEmails],
  );

  return (
    <Dialog open={open} onClose={onCancel} maxWidth='xs' fullWidth>
      <Form<Values>
        initialValues={initialValues}
        onSubmit={(values) =>
          onConfirm({
            enabled: values.sendReport,
            emails: values.sendReport ? values.reportEmails : [],
          })
        }
      >
        {({ handleSubmit, values }) => (
          <form onSubmit={handleSubmit} noValidate>
            <Stack sx={{ p: 3, pb: 2, gap: 0.75 }}>
              <Typography
                component='b'
                sx={{ fontSize: 19, fontWeight: 700, lineHeight: 1.3 }}
              >
                {__('Report po uzavretí')}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {isOver
                  ? __(
                      'Hlasovanie je už uzavreté, takže report odíde krátko po uložení.',
                    )
                  : __(
                      'Po uzavretí hlasovania odíde na zadané adresy mail s reportom v PDF a dátami v CSV.',
                    )}
              </Typography>
            </Stack>

            <Divider />

            <Stack sx={{ px: 3, py: 2.5, gap: 1.5 }}>
              <Field name='sendReport' type='checkbox'>
                {({ input }) => (
                  <SwitchField
                    label={__('Odoslať report po ukončení hlasovania')}
                    checked={!!input.checked}
                    onChange={(checked) => input.onChange(checked)}
                  />
                )}
              </Field>

              {values.sendReport && (
                <FormEmailListField
                  name='reportEmails'
                  label={__('Komu sa report pošle')}
                  required
                  validate={(emails) =>
                    // Prázdne pole je truthy, takže `required` by ho pustilo.
                    // Server aj CHECK v databáze tú kombináciu odmietajú.
                    Array.isArray(emails) && emails.length > 0
                      ? undefined
                      : __('Zadajte aspoň jednu adresu.')
                  }
                />
              )}
            </Stack>

            <Divider />

            <Stack
              direction='row'
              alignItems='center'
              gap={1}
              sx={{ px: 3, py: 2 }}
            >
              <Button variant='text' onClick={onCancel} disabled={loading}>
                {__('Zrušiť')}
              </Button>
              <Button type='submit' loading={loading} sx={{ ml: 'auto' }}>
                {__('Uložiť nastavenie')}
              </Button>
            </Stack>
          </form>
        )}
      </Form>
    </Dialog>
  );
};

export default VotingReportDialog;
